import React, { useEffect, useRef } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { AnimatePresence, motion } from 'framer-motion';
import { SizesType } from 'types/Sizes';
import { cssTheme } from '../../../themes';
import { closeOnEscape } from '../../../utils/closeOnEscape';
import transitions from '../../../utils/framerTransitions';
import { Button } from '../Button';
import { BottomBar } from '../helpers/BottomBar';
import { Portal } from '../helpers/Portal';
import { TitleBar, TitleBarProps, TitleBarVariant } from '../helpers/TitleBar';
import { Icon } from '../Icon';
import { ANIMATION_TYPES } from '../Overlay';
import useBodyScrollLock from '../Overlay/useBodyScrollLock';
type PopUpSize = Extract<SizesType, 'md' | 'lg'>;
interface Props {
  /** The body of the pop-up */
  children: React.ReactNode;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** The callback for the close button click */
  closeAction?: () => void;
  /** The data-id attribute for testing */
  dataId?: string;
  /** The footer of the pop-up. Commonly used for action button placement */
  footer?: React.ReactNode;
  /** Whether the BodyWrapper should have paddings around it */
  noPaddings?: boolean;
  /** The height of the pop-up. This will be limited by the window height. */
  height?: number;
  /** Whether the pop-up is open or not */
  isOpen: boolean;
  /** The max-width of the pop-up. This will be limited by the default prop value. */
  width?: number;
  /** The size of the PopUp header */
  size?: PopUpSize;
  /** All the props from the TitleBar can be applied here, check [TitleBar](/?path=/docs/helpers-titlebar--default) docs  */
  titleBarProps?: TitleBarProps;
  /** Determine the variant for the Header (using TitleBar underneath)  */
  headerVariant?: TitleBarVariant;
  /** The content of the overlay sub header */
  subHeaderContent?: React.ReactNode;
  /** Whether the PopUp should call the onClose closeAction when the Esc key is pressed (true by default) */
  closeOnEsc?: boolean;
  /** Whether the PopUp should call the onClose closeAction when the user clicks outside the PopUp (true by default) */
  closeOnOutsideClick?: boolean;
  /** Style prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledPopUp = styled(PopUp)` */
  style?: React.CSSProperties;
}
export const TRANSITION_DURATION = 300; // ms
const _exp = /*#__PURE__*/() => motion.div;
const Wrapper = /*#__PURE__*/styled(_exp())({
  name: "Wrapper",
  class: "w1vwarss",
  propsAsIs: true
});
const _exp4 = /*#__PURE__*/() => motion.div;
const _exp5 = /*#__PURE__*/() => ({
  height
}) => height ? `${height}px` : 'auto';
const _exp6 = /*#__PURE__*/() => ({
  height
}) => height || `calc(100vh - ${cssTheme.layout.spacing.s950})`;
const _exp7 = /*#__PURE__*/() => ({
  maxWidth
}) => `${maxWidth}px`;
export const Content = /*#__PURE__*/styled(_exp4())({
  name: "Content",
  class: "cxxgqoa",
  propsAsIs: true,
  vars: {
    "cxxgqoa-0": [_exp5()],
    "cxxgqoa-1": [_exp6()],
    "cxxgqoa-2": [_exp7()]
  }
});
const _exp11 = /*#__PURE__*/() => ({
  header
}) => !header ? '0px' : 'auto';
export const HeaderWrapper = /*#__PURE__*/styled('div')({
  name: "HeaderWrapper",
  class: "h1kf0ajj",
  propsAsIs: false,
  vars: {
    "h1kf0ajj-0": [_exp11()]
  }
});
const getPaddings = (hasHeader: boolean, noPaddings: boolean, footer: boolean) => {
  if (noPaddings) {
    return '0';
  } else if (hasHeader && !footer) {
    return `0 ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s500}`;
  } else if (hasHeader && footer) {
    return `0 ${cssTheme.layout.spacing.s500}`;
  }
  return `${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500}`;
};
const _exp12 = /*#__PURE__*/() => ({
  hasHeader,
  noPaddings,
  footer
}) => getPaddings(hasHeader, noPaddings, footer);
export const BodyWrapper = /*#__PURE__*/styled('div')({
  name: "BodyWrapper",
  class: "bsljj5t",
  propsAsIs: false,
  vars: {
    "bsljj5t-0": [_exp12()]
  }
});
export const Body = /*#__PURE__*/styled('div')({
  name: "Body",
  class: "bj66l8h",
  propsAsIs: false
});
export const Footer = /*#__PURE__*/styled('div')({
  name: "Footer",
  class: "fd2y1ez",
  propsAsIs: false
});
export const PopUp = ({
  children,
  className,
  closeAction,
  closeOnEsc = true,
  closeOnOutsideClick = true,
  dataId,
  footer,
  headerVariant,
  height,
  isOpen,
  noPaddings = false,
  size = 'md',
  subHeaderContent,
  titleBarProps,
  width = 418,
  style
}: Props) => {
  const popUpRef = useRef<HTMLDivElement>(null);
  const isCloseVisible = Boolean(closeAction);
  const popUpHeight = () => {
    if (window.innerHeight > 1040 && height && height >= 960) {
      return 960;
    } else if (height && height > window.innerHeight) {
      return window.innerHeight - 80;
    }
    return height;
  };
  const closeButton = <Button variant={headerVariant === 'inverse' ? 'transparent' : 'quiet'} size="lg" hasTouchArea dataId="PopUpCloseButton" iconEnd={<Icon name="Close" size="lg" colorPath={headerVariant === 'inverse' ? 'content.neutral.c000' : 'content.neutral.c950'} />} onClick={closeAction} />;
  const endContentButton = closeButton;
  useBodyScrollLock({
    ref: popUpRef,
    isEnabled: isOpen
  });
  useEffect(() => {
    if (closeAction && closeOnEsc && isOpen) {
      closeOnEscape({
        onClose: closeAction
      });
    }
  }, [closeAction, closeOnEsc, isOpen]);
  return <Portal>
      <AnimatePresence>
        {isOpen && <Wrapper className={className} variants={transitions.overlayFade} initial="exited" animate="entered" exit="exited" onClick={closeOnOutsideClick ? closeAction : () => null} style={style}>
            <div>
              <Content maxWidth={width} height={popUpHeight()} data-id={dataId} variants={transitions[ANIMATION_TYPES.SLIDE_DOWN]} initial="exited" animate="entered" exit="exited" onClick={e => e.stopPropagation()} transition={{
            duration: TRANSITION_DURATION
          }}>
                <HeaderWrapper header={titleBarProps && titleBarProps.centerContent}>
                  <TitleBar endContent={isCloseVisible ? endContentButton : undefined} bottomContent={subHeaderContent} variant={headerVariant} centerContent={titleBarProps?.centerContent} size={size} {...titleBarProps} />
                </HeaderWrapper>

                <BodyWrapper ref={popUpRef} hasHeader={Boolean(titleBarProps)} noPaddings={noPaddings} footer={Boolean(footer)} size={size} className={cx(size === 'lg' && '--size-lg')}>
                  <Body>{children}</Body>
                </BodyWrapper>

                {footer && <BottomBar sticky size={size}>
                    {footer}
                  </BottomBar>}
              </Content>
            </div>
          </Wrapper>}
      </AnimatePresence>
    </Portal>;
};

require("./PopUp.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PopUp.tsx");